/**
 * v1.5 C-1.b #2：录入回款弹窗
 * 流程：① 上传付款截图 → /attachments/upload 拿 url
 *      ② POST /payments/:id/records { type, amount, screenshotUrl, paidAt }
 *
 * 支持 paste/drag/选文件 三模式（沿用 ScreenshotAllocationModal 的 useLayoutEffect 模式）
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal as AntdModal, Input, Select, DatePicker, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { $, api } from '../lib/api';
import { Btn } from './ui';

interface Props {
  open: boolean;
  payment: { id: number; customerId: number; totalDue: number; totalPaid: number; balanceDue: number; orderType: string } | null;
  onClose: () => void;
}

const TYPE_OPTIONS = [
  { value: 'deposit', label: '定金' },
  { value: 'balance', label: '尾款' },
  { value: 'full', label: '全款' },
  { value: 'refund', label: '退款' },
];

export function PaymentRecordModal({ open, payment, onClose }: Props) {
  const qc = useQueryClient();
  const [type, setType] = useState<string>('deposit');
  const [amount, setAmount] = useState<string>('');
  const [paidAt, setPaidAt] = useState<dayjs.Dayjs | null>(dayjs());
  const [screenshotUrl, setScreenshotUrl] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // 弹窗打开后自动聚焦金额输入框，省去一次点击
  const amountRef = useRef<any>(null);

  useEffect(() => {
    if (open && payment) {
      // 默认建议类型
      setType(payment.orderType === 'full' ? 'full' : (payment.totalPaid > 0 ? 'balance' : 'deposit'));
      setAmount(String(payment.balanceDue ?? ''));
      setPaidAt(dayjs());
      setScreenshotUrl('');
      setPreviewUrl(null);
    }
  }, [open, payment?.id]);

  const upload = useMutation({
    mutationFn: async (file: File | Blob) => {
      const fObj = file instanceof File ? file : new File([file], `record-${Date.now()}.png`, { type: file.type });
      if (fObj.size > 8 * 1024 * 1024) throw new Error('图片超 8MB');
      const fd = new FormData();
      fd.append('file', fObj);
      fd.append('entityType', 'payment');
      fd.append('entityId', String(payment?.id ?? 0));
      fd.append('fieldName', 'screenshot');
      const r = await api.post('/attachments/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data as { url?: string; ossUrl?: string };
    },
    // 同根修：upload 返回实体字段是 ossUrl（非 url）——读 ossUrl 兜底，否则 screenshotUrl 空、记录存不上
    onSuccess: (r) => { setScreenshotUrl(r.url ?? r.ossUrl ?? ''); message.success('截图已上传'); },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '上传失败'),
  });

  const handleImageFile = (f: File | Blob) => {
    if (previewUrl?.startsWith('blob:')) {
      try { URL.revokeObjectURL(previewUrl); } catch {}
    }
    setPreviewUrl(URL.createObjectURL(f instanceof File ? f : new File([f], 'paste.png', { type: f.type })));
    upload.mutate(f);
  };

  // 全局 paste 监听（modal 打开期间）
  useLayoutEffect(() => {
    if (!open) return;
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const blob = items[i].getAsFile();
          if (blob) { handleImageFile(blob); e.preventDefault(); break; }
        }
      }
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [open]);

  const submit = useMutation({
    mutationFn: () => $.post(`/payments/${payment?.id}/records`, {
      type,
      amount: Number(amount),
      screenshotUrl,
      paidAt: paidAt?.toISOString(),
    }),
    onSuccess: () => {
      message.success('已提交，待销售主管审批确认后才入账');
      qc.invalidateQueries({ queryKey: ['/payments'] });
      qc.invalidateQueries({ queryKey: ['payment-detail', payment?.id] });
      onClose();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '提交失败'),
  });

  const canSubmit = !!(screenshotUrl && Number(amount) > 0 && paidAt);

  return (
    <AntdModal open={open} title={`录入回款 · 收款单 #${payment?.id ?? '—'}`}
               onCancel={onClose}
               afterOpenChange={(o) => { if (o) amountRef.current?.focus?.(); }}
               footer={
                 <>
                   <Btn kind="default" onClick={onClose}>取消</Btn>
                   <Btn kind="primary" onClick={() => submit.mutate()}
                        disabled={!canSubmit || submit.isPending}>
                     {submit.isPending ? '提交中…' : '提交审批'}
                   </Btn>
                 </>
               }
               width={520}>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
        应收 ¥{Number(payment?.totalDue ?? 0).toLocaleString()} ·
        已收 ¥{Number(payment?.totalPaid ?? 0).toLocaleString()} ·
        待收 ¥{Number(payment?.balanceDue ?? 0).toLocaleString()}
      </div>

      {/* 截图上传 */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, marginBottom: 6 }}>付款截图（必填）</div>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f && f.type.startsWith('image/')) handleImageFile(f);
          }}
          style={{
            border: '2px dashed var(--border)', borderRadius: 8,
            padding: previewUrl ? 12 : 24, textAlign: 'center',
            background: '#fafbfc',
          }}>
          {previewUrl ? (
            <>
              <img src={previewUrl} alt="预览" style={{ maxHeight: 180, maxWidth: '100%', borderRadius: 4 }} />
              <div style={{ marginTop: 8, fontSize: 12, color: upload.isPending ? 'var(--primary)' : 'var(--success-text)' }}>
                {upload.isPending ? '⬆️ 上传中…' : '✓ 已上传'}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 28 }}>📷</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', margin: '8px 0' }}>拖拽 / Ctrl+V / 选文件</div>
              <label style={{ color: 'var(--primary)', cursor: 'pointer' }}>
                选择本地文件
                <input type="file" accept="image/*" style={{ display: 'none' }}
                       onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f); }} />
              </label>
            </>
          )}
        </div>
      </div>

      {/* 类型 / 金额 / 时间 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>类型</div>
          <Select value={type} onChange={setType} options={TYPE_OPTIONS} style={{ width: '100%' }} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>金额（元）</div>
          <Input ref={amountRef} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="例如 5000" />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>到账时间</div>
        <DatePicker showTime value={paidAt} onChange={setPaidAt} style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
        💡 后端会自动用阿里云 OCR 识别截图金额，与你填的金额对账（pass/mismatch/failed）。
      </div>
      <div style={{ fontSize: 11, color: 'var(--warning-text, #ad6800)', marginTop: 6, padding: '6px 8px', background: '#fffbe6', borderRadius: 4 }}>
        ⚠️ 提交后需销售主管审批确认才入账；审批通过前不计入已收款、不推进成交。
      </div>
    </AntdModal>
  );
}
